import { useState, useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Lock, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ErrorMessage } from '@/components/ui/error-message';
import { authApi } from '@/api/auth';
import { toast } from '@/components/ui/use-toast';
import Routes from '@/router/routes';
import TelletLogo from '@/assets/logo/tellet-logo-main.svg';
import { passwordSchema, confirmPasswordSchema } from '@/lib/validationSchemas';
import {
  getErrorMessage,
  isNetworkError,
  isUnauthorizedError,
  isNotFoundError,
  isValidationError,
} from '@/utils/errorUtils';

const validationSchema = Yup.object().shape({
  newPassword: passwordSchema,
  confirmPassword: confirmPasswordSchema('newPassword'),
});

type FormData = Yup.InferType<typeof validationSchema>;

const defaultValues: FormData = {
  newPassword: '',
  confirmPassword: '',
};

export default function ResetPassword() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [apiError, setApiError] = useState('');
  const redirectTimerRef = useRef<ReturnType<typeof setTimeout>>();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormData>({
    resolver: yupResolver(validationSchema),
    defaultValues,
  });

  useEffect(() => {
    if (!token) {
      toast({
        title: 'Invalid link',
        description: 'The password reset link is invalid or missing.',
        variant: 'destructive',
      });
      navigate(Routes.login.path);
    }
  }, [token, navigate]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
      }
    };
  }, []);

  const onFormSubmit = async (data: FormData) => {
    if (!token) return;

    setIsLoading(true);
    setApiError('');

    try {
      await authApi.resetPassword(token, data.newPassword);
      setIsSuccess(true);
      toast({
        title: 'Password reset successful',
        description: 'Your password has been updated. Redirecting to login...',
      });

      redirectTimerRef.current = setTimeout(() => {
        navigate(Routes.login.path);
      }, 3000);
    } catch (err) {
      // TELV1-370 — branch on the actual error instead of swallowing it.
      // The network-failure branch is the load-bearing one: it tells the
      // admin their password MAY have been saved (request reached the
      // server, response didn't reach the browser) so login is the right
      // first retry. Without it, admins assume reset failed and escalate
      // to support. See TELV1-334 (Nongnoot, Thailand) for the reference
      // incident on the dashboard sibling.
      //
      // IMPORTANT: do NOT call `setIsSuccess(true)` in any branch — the
      // success screen at line 95 would render and the admin would think
      // the reset completed despite the failure.
      if (isNetworkError(err)) {
        const networkMessage =
          'Network error — your password may have been saved. Try logging in. If that fails, request a new reset link.';
        toast({ title: 'Network error', description: networkMessage, variant: 'destructive' });
        setApiError(networkMessage);
        return;
      }
      if (isUnauthorizedError(err)) {
        // Reset token expired / invalid signature / missing. Direct the
        // admin back to forgot-password.
        toast({
          title: 'Link expired',
          description: 'Your reset link has expired. Please request a new one.',
          variant: 'destructive',
        });
        navigate(Routes.forgot_password.path);
        return;
      }
      if (isNotFoundError(err)) {
        // Account deleted between forgot-password and submit (extreme edge).
        toast({
          title: 'Account not found',
          description: 'Please request a new reset link.',
          variant: 'destructive',
        });
        navigate(Routes.forgot_password.path);
        return;
      }
      if (isValidationError(err)) {
        // 400 — surface the server message verbatim (DTO validation, etc.)
        const validationMessage = getErrorMessage(
          err,
          'Invalid input. Please check your password.',
        );
        toast({
          title: 'Invalid input',
          description: validationMessage,
          variant: 'destructive',
        });
        setApiError(validationMessage);
        return;
      }
      // 500 / 502 / 503 / 504 / any unhandled 4xx — show the server message
      // when available; getErrorMessage already maps status to friendly copy.
      const fallbackMessage = getErrorMessage(
        err,
        'Failed to reset password. Please try again or contact support.',
      );
      toast({ title: 'Error', description: fallbackMessage, variant: 'destructive' });
      setApiError(fallbackMessage);
    } finally {
      setIsLoading(false);
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <img src={TelletLogo} alt="Tellet" className="mx-auto h-12 w-auto dark:invert" />
            <div className="mt-6 mx-auto h-16 w-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
              <Check size={32} className="text-green-600 dark:text-green-400" />
            </div>
            <h2 className="mt-6 text-3xl font-bold text-foreground">Password reset successful</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Your password has been updated. You can now sign in with your new password.
            </p>
          </div>

          <div className="rounded-md bg-green-50 dark:bg-green-900/30 p-4">
            <p className="text-sm text-green-700 dark:text-green-400">
              Redirecting to login page...
            </p>
          </div>

          <Button onClick={() => navigate(Routes.login.path)} className="w-full">
            Go to login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-background py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img src={TelletLogo} alt="Tellet" className="mx-auto h-12 w-auto dark:invert" />
          <h2 className="mt-6 text-3xl font-bold text-foreground">Reset your password</h2>
          <p className="mt-2 text-sm text-muted-foreground">
            Choose a new password for your account. Make sure it&apos;s at least 8 characters long.
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onFormSubmit)}>
          <div className="space-y-4">
            <div>
              <label
                htmlFor="new-password"
                className="block text-sm font-medium text-foreground mb-1"
              >
                New password
              </label>
              <Input
                id="new-password"
                type="password"
                autoComplete="new-password"
                placeholder="Enter new password (min 8 characters)"
                {...register('newPassword')}
                icon={<Lock size={20} />}
                hasError={!!errors.newPassword}
              />
              <ErrorMessage message={errors.newPassword?.message} />
            </div>

            <div>
              <label
                htmlFor="confirm-password"
                className="block text-sm font-medium text-foreground mb-1"
              >
                Confirm password
              </label>
              <Input
                id="confirm-password"
                type="password"
                autoComplete="new-password"
                placeholder="Confirm your new password"
                {...register('confirmPassword')}
                icon={<Lock size={20} />}
                hasError={!!errors.confirmPassword}
              />
              <ErrorMessage message={errors.confirmPassword?.message} />
            </div>
          </div>

          {apiError && (
            <div className="rounded-md bg-red-50 dark:bg-red-900/30 p-4">
              <p className="text-sm text-red-700 dark:text-red-400">{apiError}</p>
            </div>
          )}

          <Button type="submit" className="w-full" disabled={isLoading}>
            {isLoading ? 'Resetting password...' : 'Reset password'}
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => navigate(Routes.login.path)}
          >
            Back to login
          </Button>
        </form>
      </div>
    </div>
  );
}
